import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, LifeBuoy, Paperclip, Send, X } from "lucide-react";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import SupStatusBadge from "../../../components/general/SupportSharedFiles/SupStatusBadge";
import SupEmptyState from "../../../components/general/SupportSharedFiles/SupEmptyState";
import SupLoader from "../../../components/general/SupportSharedFiles/SupLoader";
import "../../../assets/css/general/Support/supshare.css";
import "../../../assets/css/customer/customerSupport/cussupportcreate.css";


const HOTEL_CATEGORIES = [
  "Booking Issue",
  "Payment Problem",
  "Refund Request",
  "Hotel Complaint",
  "Room Quality Issue",
  "Service Quality",
  "Cancellation Issue",
  "Other",
];
 
const PACKAGE_CATEGORIES = [
  "Booking Issue",
  "Payment Problem",
  "Refund Request",
  "Package Complaint",
  "Itinerary Issue",
  "Guide Complaint",
  "Cancellation Issue",
  "Other",
];
 
const CusSupportCreate = () => {
  const { baseUrl, authToken } = useContext(AppContext);
  const navigate = useNavigate();
 
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
 
  const [form, setForm] = useState({
    jjbooking_id: "",
    category: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
 
  useEffect(() => {
    axios
      .get(`${baseUrl}/customer/support/eligible-bookings`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setBookings(res.data.bookings || []);
      })
      .catch((err) => {
        console.error(err.response?.data || err.message);
        setError("Failed to load your bookings.");
      })
      .finally(() => setBookingsLoading(false));
  }, [authToken]);
 
  const selectedBooking = bookings.find(
    (b) => String(b.id) === String(form.jjbooking_id)
  );
 
  const isPackageBooking = selectedBooking?.booking_type === "packages";
  const CATEGORIES = isPackageBooking ? PACKAGE_CATEGORIES : HOTEL_CATEGORIES;
  const handleChange = (e) => {
  const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };
 
 
  const validate = () => {
    const e = {};
    if (!form.jjbooking_id) e.jjbooking_id = "Please select a booking.";
    if (!form.category) e.category = "Please select a category.";
    if (!form.description.trim()) e.description = "Description is required.";
    else if (form.description.trim().length < 20)
      e.description = "Please describe your issue in at least 20 characters.";
    return e;
  };
 
  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }
    setSubmitting(true);
    setError("");
 
    const formData = new FormData();
    formData.append("jjbooking_id", form.jjbooking_id);
    formData.append("category", form.category);
    formData.append("description", form.description);
 
    axios
      .post(`${baseUrl}/customer/support/tickets`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        setSuccess("Support ticket created successfully!");
        setTimeout(() => navigate("/customer/support/tickets"), 1500);
      })
      .catch((err) => {
        const serverErrors = err.response?.data?.errors;
        if (serverErrors) setErrors(serverErrors);
        else setError(err.response?.data?.message || "Failed to create ticket.");
      })
      .finally(() => setSubmitting(false));
  };
 
  if (bookingsLoading) {
    return (
      <div className="cus-sc-page">
        <SupLoader label="Loading your bookings..." />
      </div>
    );
  }
 
  return (
    <div className="cus-sc-page">
      {/* Header */}
      <div className="cus-sc-header">
        <button className="cus-sc-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back
        </button>
        <div>
          <h2 className="cus-sc-heading">Create Support Ticket</h2>
          <p className="cus-sc-subtext">Describe your issue and we'll resolve it quickly</p>
        </div>
      </div>
 
      {error && <div className="sup-error-banner">{error}</div>}
      {success && <div className="cus-sc-success-banner">{success}</div>}
 
      <div className="cus-sc-layout">
        <div className="cus-sc-card">
          <div className="cus-sc-card__head">
            <LifeBuoy size={18} />
            <h3>Ticket Details</h3>
          </div>
 
          {/* Booking Select */}
          <div className="cus-sc-field">
            <label className="cus-sc-label">Select Booking *</label>
            <select
              className={`cus-sc-select ${errors.jjbooking_id ? "cus-sc-input--error" : ""}`}
              name="jjbooking_id"
              value={form.jjbooking_id}
              onChange={handleChange}
            >
              <option value="">Select a booking</option>
              {bookings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.booking_type === "packages"
                    ? b.Jjpackage?.package_name || "Package Booking"
                    : b.jjroom?.room_type || "Hotel Booking"}{" "}
                  | {b.booking_status}
                </option>
              ))}
            </select>
            {errors.jjbooking_id && (
              <p className="cus-sc-err">{errors.jjbooking_id}</p>
            )}
          </div>
 
          {/* Category Select */}
          <div className="cus-sc-field">
            <label className="cus-sc-label">Issue Category *</label>
            <select
              className={`cus-sc-select ${errors.category ? "cus-sc-input--error" : ""}`}
              name="category"
              value={form.category}
              onChange={handleChange}
            >
              <option value="">Select category</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {errors.category && <p className="cus-sc-err">{errors.category}</p>}
          </div>
 
          {/* Description */}
          <div className="cus-sc-field">
            <label className="cus-sc-label">Description *</label>
            <textarea
              className={`cus-sc-textarea ${errors.description ? "cus-sc-input--error" : ""}`}
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe the issue in detail..."
              rows={5}
            />
            <span className="cus-sc-charcount">{form.description.length} / 2000</span>
            {errors.description && <p className="cus-sc-err">{errors.description}</p>}
          </div>
 
 
          <button
            className="cus-sc-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <><div className="cus-sc-btn-spinner" /> Submitting...</>
            ) : (
              <><Send size={15} /> Submit Ticket</>
            )}
          </button>
        </div>
 
        {selectedBooking && (
          <div className="cus-sc-preview">
            <div className="cus-sc-preview__head">
              Booking Summary
              <span className={`cus-sc-preview__type-badge ${isPackageBooking ? "cus-sc-preview__type-badge--package" : "cus-sc-preview__type-badge--hotel"}`}>
                {isPackageBooking ? "Package" : "Hotel"}
              </span>
            </div>
            <div className="cus-sc-preview__body">
              <div className="cus-sc-preview__row">
                <span>Booking ID</span>
                <span>#{selectedBooking.id}</span>
              </div>
              <div className="cus-sc-preview__row">
                <span>Type</span>
                <span className="cus-sc-preview__cap">{selectedBooking.booking_type}</span>
              </div>
              {selectedBooking.booking_type === "packages" ? (
                <div className="cus-sc-preview__row">
                  <span>Package</span>
                  <span>{selectedBooking.Jjpackage?.package_name || "—"}</span>
                </div>
              ) : (
                <div className="cus-sc-preview__row">
                  <span>Room</span>
                  <span>{selectedBooking.jjroom?.room_type || "—"}</span>
                </div>
              )}
              {isPackageBooking ? (
                <div className="cus-sc-preview__row cus-sc-preview__row--info">
                  <span>Handled By</span>
                  <span className="cus-sc-preview__badge">Admin</span>
                </div>
              ) : (
                <div className="cus-sc-preview__row">
                  <span>Vendor</span>
                  <span>{selectedBooking.Jjvendor?.name || "—"}</span>
                </div>
              )}
              <div className="cus-sc-preview__row">
                <span>Check-In</span>
                <span>{selectedBooking.checkIn || "—"}</span>
              </div>
              <div className="cus-sc-preview__row">
                <span>Amount</span>
                <span>PKR {Number(selectedBooking.total_amount).toLocaleString()}</span>
              </div>
              <div className="cus-sc-preview__row">
                <span>Status</span>
                <span className="cus-sc-preview__cap">{selectedBooking.booking_status}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
 
export default CusSupportCreate;